import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, usePaginatedQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '@/lib/utils'
import {
  Plus, 
  Search, 
  Eye, 
  FileText,
  Calendar,
  User,
  Euro,
  CheckCircle
} from 'lucide-react'
import type { Id } from '../../../convex/_generated/dataModel'
import { prescriptionDisplayNumber } from '../../../convex/lib/prescriptionNumber'
import { formatRequestedAppointment } from '../../../shared/deliveryAppointment'
import { LaboratoryTabs, useLaboratoryTab } from '@/components/externalLabs/LaboratoryTabs'
import { ExternalPrescriptionList } from '@/components/externalLabs/ExternalPrescriptionList'

export function PrescriptionList() {
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const primoLab = hasPermission('view_prescriptions')
  const external = hasPermission('view_external_prescriptions')
  const { tab, setTab } = useLaboratoryTab(primoLab)
  const showExternal = external && tab === 'external'
  return (
    <div className="space-y-6">
      <LaboratoryTabs value={showExternal ? 'external' : 'primolab'} onChange={setTab} primoLab={primoLab} external={external} />
      {showExternal ? (
        <div className="space-y-6" key={profile?.company_id}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div><h1 className="text-2xl font-bold text-gray-900">Prescrizioni esterne</h1><p className="mt-1 text-gray-600">Lavori affidati ai laboratori della tua clinica.</p></div>
            {hasPermission('create_external_prescription') && <Button asChild variant="primoup" className="text-gray-950"><Link to="/prescriptions/new"><Plus className="mr-2 h-4 w-4" />Nuova prescrizione</Link></Button>}
          </div>
          <ExternalPrescriptionList />
        </div>
      ) : primoLab ? <PrimoLabPrescriptionList /> : null}
    </div>
  )
}

function PrimoLabPrescriptionList() {
  const { profile } = useAuth()
  const { canCreatePrescriptions, hasPermission } = usePermissions()
  const canViewAll = hasPermission('view_all_prescriptions')
  const canManagePhasePolicies = hasPermission('manage_phase_policies')
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')
  const pageSize = 20
  const isSearching = searchInput.trim().length > 0
  const companyId = canViewAll
    ? (selectedCompanyId || undefined) as Id<'companies'> | undefined
    : profile?.company_id

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchTerm(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  // Load every permitted clinic, including those beyond the first 100.
  const companies = useQuery(api.companies.listCompanies, canViewAll ? {} : 'skip')
  const companyRows = Array.isArray(companies) ? companies : companies?.page ?? []
  const pendingPhaseApprovals = useQuery(
    api.companyPhases.listPendingPhaseApprovals,
    canManagePhasePolicies ? {} : 'skip'
  )

  const { results: prescriptions, status: paginationStatus, loadMore } = usePaginatedQuery(
    api.prescriptions.listPrescriptions,
    profile && !isSearching ? {
      company_id: companyId,
      status: statusFilter || undefined,
    } : 'skip',
    { initialNumItems: pageSize }
  )

  const searchResults = useQuery(
    api.prescriptions.searchPrescriptions,
    profile && isSearching && searchTerm && searchTerm === searchInput.trim() ? {
      searchTerm,
      company_id: companyId,
      status: statusFilter || undefined,
    } : 'skip'
  )
  const displayPrescriptions = isSearching
    ? searchResults
    : paginationStatus === 'LoadingFirstPage' ? undefined : prescriptions

  const statusOptions = [
    { value: '', label: 'Tutti gli stati' },
    { value: 'creata', label: 'Creata' },
    { value: 'in_attesa_approvazione', label: 'In attesa approvazione fase' },
    { value: 'inviata', label: 'Inviata' },
    { value: 'in_lavorazione', label: 'In Lavorazione' },
    { value: 'spedita', label: `${getStatusLabel('spedita')} (fase intermedia)` },
    { value: 'completata', label: getStatusLabel('completata') },
    { value: 'annullata', label: 'Annullata' },
  ]
  
  if (!profile) {
    return <Loading text="Caricamento..." />
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prescrizioni</h1>
          <p className="text-gray-600 mt-1">
            Gestisci e monitora le tue prescrizioni dentali
          </p>
        </div>
        
        {canCreatePrescriptions && (
          <Link to="/prescriptions/new">
            <Button variant="primoup">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Prescrizione
            </Button>
          </Link>
        )}
      </div>

      {canManagePhasePolicies && pendingPhaseApprovals && pendingPhaseApprovals.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardHeader>
            <CardTitle>Salti fase da approvare ({pendingPhaseApprovals.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingPhaseApprovals.slice(0, 10).map((item) => (
              <Link
                key={item._id}
                to={`/prescriptions/${item._id}`}
                className="flex items-center justify-between rounded border bg-white px-3 py-2 text-sm hover:border-amber-400"
              >
                <span>
                  <strong>{prescriptionDisplayNumber(item)}</strong> · {item.company_name} · {item.service_name}
                  {item.lab_api_last_response?.sync_status === 'group_partial' && (
                    <span className="ml-2 font-medium text-red-700">Gruppo parziale · retry</span>
                  )}
                </span>
                <span className="text-amber-800">Apri richiesta</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="search"
                  aria-label="Cerca prescrizioni per numero PrimoUp o nome paziente"
                  placeholder="Numero PrimoUp o nome paziente"
                  value={searchInput}
                  className="pl-10"
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            
            {/* Company filter for admin / tecnico lab */}
            {canViewAll && (
              <div className="sm:w-64">
                <select
                  aria-label="Filtra per clinica"
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Tutte le cliniche</option>
                  {companyRows.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.nome}{company.suffix ? ` · ${company.suffix}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="sm:w-48">
              <select
                aria-label="Filtra per stato"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Prescriptions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Lista Prescrizioni</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isSearching && displayPrescriptions?.length === 100 && (
            <p role="status" className="mb-4 text-sm text-gray-600">
              Mostrati 100 risultati. Specifica meglio il numero, il paziente o la clinica per restringere la ricerca.
            </p>
          )}
          {displayPrescriptions ? (
            displayPrescriptions.length > 0 ? (
              <div className="space-y-4">
                {displayPrescriptions.map((prescription) => (
                  <div 
                    key={prescription._id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <h3 className="break-all font-semibold text-lg">
                            {prescriptionDisplayNumber(prescription)}
                          </h3>
                          <Badge
                            className={
                              prescription.stato === 'spedita' &&
                              (prescription as any).attesa_prossima_prova === true
                                ? 'bg-amber-100 text-amber-900 border border-amber-200'
                                : getStatusColor(prescription.stato)
                            }
                          >
                            {prescription.stato === 'spedita' &&
                            (prescription as any).attesa_prossima_prova === true
                              ? 'Spedita dal laboratorio · attesa conferma prova'
                              : getStatusLabel(prescription.stato)}
                          </Badge>
                          {(prescription as any).richiede_nuova_prova_post_esclusione === true && (
                            <Badge className="bg-red-100 text-red-800 border border-red-200">
                              Nuova prova richiesta
                            </Badge>
                          )}
                          {prescription.external_prescription_id && (
                            <div className="flex items-center space-x-1 px-2 py-1 bg-green-50 text-green-700 rounded-md border border-green-200">
                              <CheckCircle className="w-3 h-3" />
                              <span className="text-xs font-medium">PrimoUp</span>
                            </div>
                          )}
                        </div>
                        
                        <div className={`grid grid-cols-1 sm:grid-cols-2 ${hasPermission('view_prices') ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-x-4 gap-y-3 text-sm text-gray-600`}>
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>{prescription.NomePaziente}</span>
                          </div>
                          
                          <div className="flex items-start gap-2">
                            <Calendar aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                            <div>
                              <span className="block text-xs">Creata il</span>
                              <span>{formatDate(prescription.created_at)}</span>
                            </div>
                          </div>

                          <div className="flex items-start gap-2">
                            <Calendar aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                            <div>
                              <span className="block text-xs">Appuntamento richiesto</span>
                              <span className="font-medium text-gray-900">
                                {formatRequestedAppointment(prescription.requested_appointment) ?? 'Non specificato'}
                              </span>
                            </div>
                          </div>
                          
                          {hasPermission('view_prices') && (
                            <div className="flex items-center space-x-2">
                              <Euro className="w-4 h-4" />
                              <span className="font-medium">
                                {formatCurrency(prescription.PrezzoNetto)}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Additional info for larger screens */}
                        <div className="hidden md:block mt-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-4">
                            <span>
                              Denti: {JSON.parse(prescription.denti_selezionati || '[]').length}
                            </span>
                            <span>
                              Impianti: {JSON.parse(prescription.impianti_selezionati || '[]').length}
                            </span>
                            {prescription.external_number_text && (
                              <span>
                                Rif. Lab: {prescription.external_number_text}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex shrink-0 flex-col space-y-2 self-end sm:self-start sm:ml-4">
                        <Link to={`/prescriptions/${prescription._id}`}>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4 mr-2" />
                            Dettagli
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Pagination */}
                {!isSearching && (paginationStatus === 'CanLoadMore' || paginationStatus === 'LoadingMore') && (
                  <div className="flex justify-center pt-4">
                    <Button 
                      variant="outline"
                      onClick={() => loadMore(pageSize)}
                      disabled={paginationStatus === 'LoadingMore'}
                    >
                      {paginationStatus === 'LoadingMore' ? 'Caricamento...' : 'Carica Altri'}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nessuna prescrizione trovata
                </h3>
                <p className="text-gray-500 mb-6">
                  {isSearching ?
                    'Nessun risultato per la ricerca corrente.' :
                    'Non hai ancora creato nessuna prescrizione.'
                  }
                </p>
                {canCreatePrescriptions && !isSearching && (
                  <Link to="/prescriptions/new">
                    <Button variant="primoup">
                      <Plus className="w-4 h-4 mr-2" />
                      Crea Prima Prescrizione
                    </Button>
                  </Link>
                )}
              </div>
            )
          ) : (
            <Loading text="Caricamento prescrizioni..." />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
